import { Injectable } from '@angular/core';
import { MatDialog, MatDialogConfig } from '@angular/material/dialog';
import { Observable, of } from 'rxjs';
import Swal from 'sweetalert2';
import { EnrichedProgrammingFile } from 'src/entities/file';
import {
  getOperationsProgrammingStateTransition,
  ProgrammingStateTransition
} from './programming-state-transition';
import { EditStateComponent } from './ui/edit-state/edit-state.component';
import { EditStateDialogData, EditStateDialogResult } from './model/edit-state-dialog.model';

@Injectable({ providedIn: 'root' })
export class ChangeFileStateService {
  constructor(private readonly dialog: MatDialog) {}

  hasManualOperationsStateChange(file: EnrichedProgrammingFile): boolean {
    return !!getOperationsProgrammingStateTransition(file.displayState).targetState;
  }

  requestStateChange(file: EnrichedProgrammingFile): Observable<EditStateDialogResult | null | undefined> {
    if (file.pendingPreOpIncidents != null && file.pendingPreOpIncidents > 0) {
      this.showPendingProgrammingReviewAlert(file);
      return of(null);
    }

    const transition = getOperationsProgrammingStateTransition(file.displayState);

    if (!transition.targetState) {
      this.showUnavailableStateChangeAlert(file, transition);
      return of(null);
    }

    const dialogConfig = new MatDialogConfig();
    const dialogData: EditStateDialogData = {
      fileId: file.id ?? undefined,
      itinerario: file,
      currentState: transition.displayState,
      state: transition.targetState,
      targetState: transition.targetState,
      operationsNextStates: transition.operationsNextStates,
      salesNextStates: transition.salesNextStates,
      automaticTransitions: transition.automaticTransitions,
      sourceArea: 'OPERATIONS',
      context: 'PROGRAMACION'
    };
    dialogConfig.data = dialogData;
    dialogConfig.width = '480px';
    dialogConfig.maxWidth = '95vw';

    return this.dialog.open(EditStateComponent, dialogConfig).afterClosed();
  }

  private showPendingProgrammingReviewAlert(file: EnrichedProgrammingFile): void {
    const pendingIncidents = file.pendingPreOpIncidents ?? 0;
    const fileLabel = this.escapeHtml(this.getFileLabel(file));

    void Swal.fire({
      title: 'Revisión de Programación pendiente',
      icon: 'warning',
      html: `
        <div style="text-align:left;color:#334155;font-size:14px;line-height:1.45">
          <div style="margin:0 auto 14px;padding:14px 16px;border-radius:14px;background:#fff7ed;border:1px solid #fed7aa">
            <div style="color:#9a3412;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase">Bloqueo de estado</div>
            <div style="margin-top:6px;color:#1f2937;font-size:16px;font-weight:700">${fileLabel}</div>
          </div>
          <div style="display:flex;align-items:center;gap:12px;margin-bottom:14px;padding:12px;border-radius:12px;background:#f8fafc">
            <div style="min-width:42px;height:42px;border-radius:999px;background:#ffedd5;color:#ea580c;display:flex;align-items:center;justify-content:center;font-size:20px;font-weight:800">${pendingIncidents}</div>
            <div>
              <div style="color:#1f2937;font-weight:700">Incidentes pre-operativos pendientes</div>
              <div style="color:#64748b">Debe revisarlos antes de avanzar el estado del file.</div>
            </div>
          </div>
          <div style="padding:12px;border-left:4px solid #e95a1a;background:#fffaf7;border-radius:10px">
            Abre la columna <strong>Revisión de Programación</strong>, resuelve los pendientes y vuelve a intentar el cambio.
          </div>
        </div>
      `,
      confirmButtonText: 'Entendido',
      confirmButtonColor: '#e95a1a',
      buttonsStyling: true,
      width: 520
    });
  }

  private showUnavailableStateChangeAlert(
    file: EnrichedProgrammingFile,
    transition: ProgrammingStateTransition
  ): void {
    const currentState = this.escapeHtml(file.displayState ?? 'SIN ESTADO');
    const safeReason = this.escapeHtml(transition.reason);
    const salesTransitionInfo = this.buildSalesTransitionInfoHtml(
      transition.salesNextStates
    );
    const automaticTransitionInfo = this.buildAutomaticTransitionInfoHtml(
      transition.automaticTransitions
    );
    const actionScopeInfo = this.buildActionScopeInfoHtml(transition);

    void Swal.fire({
      title: 'Cambio de estado no disponible',
      icon: 'info',
      html: `
        <div style="text-align:left;color:#334155;font-size:14px;line-height:1.45">
          <div style="margin-bottom:14px;padding:14px 16px;border-radius:14px;background:#eff6ff;border:1px solid #bfdbfe">
            <div style="color:#1d4ed8;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase">Operations · Programación</div>
            <div style="margin-top:8px;display:flex;align-items:center;justify-content:space-between;gap:12px">
              <span style="color:#64748b">Estado actual</span>
              <strong style="padding:6px 10px;border-radius:999px;background:#ffffff;color:#1f2937;border:1px solid #dbeafe">${currentState}</strong>
            </div>
          </div>
          <p style="margin:0 0 12px;color:#475569">${safeReason}</p>
          ${salesTransitionInfo}
          ${automaticTransitionInfo}
          ${actionScopeInfo}
        </div>
      `,
      confirmButtonText: 'Aceptar',
      confirmButtonColor: '#2563eb',
      buttonsStyling: true,
      width: 500
    });
  }

  private getFileLabel(file: EnrichedProgrammingFile): string {
    const fileNumber = [file.fileNumber, file.fileExtend].filter(Boolean).join('-');
    const fileTitle = file.nombreItinerario || file.nameClient || 'File seleccionado';

    return fileNumber ? `${fileNumber} · ${fileTitle}` : fileTitle;
  }

  private buildSalesTransitionInfoHtml(salesNextStates: string[]): string {
    if (salesNextStates.length === 0) return '';

    const stateBadges = salesNextStates
      .map(
        (state) =>
          `<span style="display:inline-flex;align-items:center;padding:5px 9px;border-radius:999px;background:#ffffff;border:1px solid #bae6fd;color:#075985;font-size:12px;font-weight:700">${this.escapeHtml(state)}</span>`
      )
      .join('');

    return `
      <div style="margin-bottom:12px;padding:12px;border-radius:12px;background:#f0f9ff;border:1px solid #bae6fd">
        <div style="margin-bottom:8px;color:#0369a1;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase">Referencia Sales para registro</div>
        <div style="display:flex;flex-wrap:wrap;gap:8px">${stateBadges}</div>
      </div>
    `;
  }

  private buildAutomaticTransitionInfoHtml(
    automaticTransitions: ProgrammingStateTransition['automaticTransitions']
  ): string {
    if (automaticTransitions.length === 0) return '';

    const transitionItems = automaticTransitions
      .map(
        (transition) => `
          <div style="padding:10px;border-radius:10px;background:#ffffff;border:1px solid #c7d2fe">
            <div style="display:flex;align-items:center;gap:8px;margin-bottom:5px">
              <strong style="color:#3730a3">${this.escapeHtml(transition.fromState)}</strong>
              <span style="color:#64748b">→</span>
              <strong style="color:#3730a3">${this.escapeHtml(transition.toState)}</strong>
            </div>
            <div style="color:#64748b;font-size:12px">${this.escapeHtml(transition.description)}</div>
          </div>
        `
      )
      .join('');

    return `
      <div style="margin-bottom:12px;padding:12px;border-radius:12px;background:#eef2ff;border:1px solid #c7d2fe">
        <div style="margin-bottom:8px;color:#3730a3;font-size:12px;font-weight:700;letter-spacing:.06em;text-transform:uppercase">Cambios automáticos del sistema</div>
        <div style="display:grid;gap:8px">${transitionItems}</div>
      </div>
    `;
  }

  private buildActionScopeInfoHtml(transition: ProgrammingStateTransition): string {
    const hasSalesTransitions = transition.salesNextStates.length > 0;
    const hasAutomaticTransitions = transition.automaticTransitions.length > 0;

    if (!hasSalesTransitions && !hasAutomaticTransitions) return '';

    const message =
      hasSalesTransitions && hasAutomaticTransitions
        ? 'Si el siguiente paso pertenece a Sales o al sistema automático, no debe ejecutarse manualmente desde Operations.'
        : hasAutomaticTransitions
          ? 'Este cambio lo ejecuta automáticamente el sistema; no debe realizarse manualmente desde Operations.'
          : 'Este cambio corresponde al flujo de Sales; no debe ejecutarse manualmente desde Operations.';

    return `
      <div style="padding:12px;border-radius:12px;background:#f8fafc;color:#64748b">
        ${message}
      </div>
    `;
  }

  private escapeHtml(value: string | number): string {
    return String(value)
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }
}
